import { filterByCategorySelector } from '~/app/store/filterByCategory'
import { defaultFilterOptionIdsSelector, filterOptionByIdSelector } from '~/app/store/filterOption'
import { filterByIdSelector } from '~/app/store/filter'
import { booleanRenderPropertySelector, selectedOptionsSelector } from '~/app/store/config'
import { filterByOptionSelector } from '~/app/store/filterByOption'
import { createSelector } from 'reselect'
import { REFINEMENT_DIMENSION, FILTER_TYPE } from 'shared/constants'
import { normalizeFilterType } from '~/app/features/Gallery/components/Filters/util'
import { hasColorSelector } from '~/app/store/designPersonalization/selectors'
import { RefinementsByType } from '~/app/store/refinement/types'
import { getUploadTileAvailabilitySelector } from '~/app/store/config/reducer'
import { StringDictionary } from 'shared/types/util'
import type { GalleryConfigApi } from 'shared/types/gallery/config-api'
import type { GalleryModelsUrl } from 'shared/types/gallery/models'
import type { State } from 'shared/types/gallery/state'
import { RenderProperty } from 'shared/renderProperties'

export const filterRefinementsSelector = createSelector(
  (state: State.GlobalState) => state.refinements,
  (refinements: State.RefinementState) =>
    Object.values(refinements).filter(
      (refinement) => refinement.dimension === REFINEMENT_DIMENSION.ATTRIBUTE || refinement.dimension === REFINEMENT_DIMENSION.CATEGORY,
    ),
)

export const attributeRefinementsSelector = createSelector(
  (state: State.GlobalState) => state.refinements,
  (refinements: State.RefinementState) => Object.values(refinements).filter((refinement) => refinement.dimension === REFINEMENT_DIMENSION.ATTRIBUTE),
)

export const categoryRefinementsSelector = createSelector(
  (state: State.GlobalState) => state.refinements,
  (refinements: State.RefinementState) => Object.values(refinements).filter((refinement) => refinement.dimension === REFINEMENT_DIMENSION.CATEGORY),
)

export const keywordRefinementSelector = (state: State.GlobalState): State.Refinement => state.refinements.keyword

export const collectionRefinementSelector = (state: State.GlobalState): State.Refinement => state.refinements.collection

export const refinementsSelector = (state: State.GlobalState): State.RefinementState => state.refinements

export const refinementsHasRefSelector = createSelector(
  (state: State.GlobalState) => state.refinements,
  (state: State.GlobalState) => state.config.metadata.categoryIds,
  (refinements, metaCategories) =>
    (refinement: string): boolean =>
      (refinements && refinement in refinements) || (metaCategories?.includes(+refinement) ?? false),
)

// This is separated from the selector because the logic is reused when reading refinements from the URL
export const refinementIdsByFilter = (
  refinements: StringDictionary<State.Refinement>,
  filterByOption: (id: string) => string,
  filterByCategory: (id: string) => string,
): GalleryModelsUrl.RefinementsByFilter =>
  Object.values(refinements).reduce(
    (memo, ref: State.Refinement) => {
      const filterName = filterByOption(ref.value)
      const catFilterName = filterByCategory(ref.value)

      switch (ref.dimension) {
        case REFINEMENT_DIMENSION.ATTRIBUTE:
          return {
            ...memo,
            attributes: {
              ...memo.attributes,
              [filterName]: [...(memo.attributes[filterName] || ([] as string[])), ref.value],
            },
          } as GalleryModelsUrl.RefinementsByFilter
        case REFINEMENT_DIMENSION.CATEGORY:
          return {
            ...memo,
            categories: {
              ...memo.categories,
              [catFilterName]: [...(memo.categories[catFilterName] || ([] as string[])), ref.value],
            },
          } as GalleryModelsUrl.RefinementsByFilter
        case REFINEMENT_DIMENSION.KEYWORD:
          return {
            ...memo,
            keyword: ref.value,
          }
        case REFINEMENT_DIMENSION.COLLECTION:
          return {
            ...memo,
            collection: ref.value,
          }
        default:
          return memo
      }
    },
    {
      attributes: {},
      categories: {},
      keyword: undefined,
      collection: undefined,
    } as GalleryModelsUrl.RefinementsByFilter,
  )

const refinementsWithoutPersonalizationColor = createSelector(
  refinementsSelector,
  hasColorSelector,
  filterByOptionSelector,
  filterByIdSelector,
  (refinements, hasPersonalizationColor, filterByOption, filterById) => {
    if (!hasPersonalizationColor) {
      return refinements
    }

    return Object.fromEntries(Object.entries(refinements).filter(([id]) => normalizeFilterType(filterById(filterByOption(id))?.type) !== FILTER_TYPE.COLOR))
  },
)

export const refinementIdsByFilterSelector = createSelector(
  refinementsWithoutPersonalizationColor,
  (state: State.GlobalState) => filterByOptionSelector(state),
  (state: State.GlobalState) => filterByCategorySelector(state),
  (refinements, filterByOption, filterByCategory) => refinementIdsByFilter(refinements, filterByOption, filterByCategory),
)

/**
 * Returns a list of selected refinement ids excluding the default values
 * If an attribute has multiple values selected for a filter with a default included,
 *   this function returns all selected values including the default
 */
export const nonDefaultAttributeRefinementIdsSelector = createSelector(
  refinementIdsByFilterSelector,
  defaultFilterOptionIdsSelector,
  (refinementsByFilter: RefinementsByType, defaultFilterOptions: string[]) =>
    Object.values(refinementsByFilter.attributes || []).reduce((memo, refinements) => {
      const nonDefaultRefinements = refinements.filter((refinement) => !defaultFilterOptions.includes(refinement))

      // If we have any nonDefault refinements, we need to include all refinements
      // otherwise the defaults will be removed when we load this url
      if (nonDefaultRefinements.length > 0) {
        memo.push(...refinements)
      }

      return memo
    }, [] as string[]),
)

/**
 * Returns a dictionary of filterOptions by filterId for filterIds with an implicit default option selected
 * Used to replace defaults with alternative options in the event of 0 results in Gallery
 * This only works if implicit default values can only be used for radio buttons
 */
export const alternativeToDefaultAttributeRefinementSelector = createSelector(
  (state: State.GlobalState) => state.refinements,
  filterByOptionSelector,
  filterByIdSelector,
  (refinements: State.RefinementState, filterByOption: (name: string) => string, filterById: (name: string) => State.Filter) =>
    Object.keys(refinements).reduce(
      (memo, refinementValue) => {
        const refinement = refinements[refinementValue]

        if (refinement.isImplicitDefault) {
          const filterId = filterByOption(refinement.value)
          // isImplicitDefault will only be set for attribute filters, so this is safe
          const filter = filterById(filterId) as State.AttributeFilter

          return {
            ...memo,
            [filter.name]: filter.options,
          }
        }
        return memo
      },
      {} as StringDictionary<string[]>,
    ),
)

export const userOptionsSelector = createSelector(
  refinementIdsByFilterSelector,
  filterOptionByIdSelector,
  selectedOptionsSelector,
  (
    refinements: { attributes: StringDictionary<string[]> },
    filterOptionById: (id: string) => GalleryConfigApi.FilterOption,
    selectedOptions: StringDictionary<string | number>,
  ): GalleryModelsUrl.UserOptions => {
    const { attributes } = refinements
    const filterOptions = Object.keys(attributes).reduce((memo, key) => {
      const options = attributes[key]

      // options with more than one value selected can't be passed to the upload flow service
      if (options.length > 1 || options.length < 1) {
        return memo
      }

      const filterOption = filterOptionById(options[0])

      // make sure we don't add pure content driving attributes
      if (filterOption && filterOption.productOption) {
        const { optionName, optionValue } = filterOption.productOption

        return {
          ...memo,
          [optionName]: optionValue,
        }
      }
      return memo
    }, {} as StringDictionary<string>)

    const userOptions: GalleryModelsUrl.UserOptions = { ...filterOptions, ...selectedOptions }

    return userOptions
  },
)

/**
 * Builds `selections` and `alternateSelections` maps for the header `PriceDisplay`
 * (`DisplayVariant.LOWEST_PRICE`) from the shopper's active refinements.
 *
 * Resolves each attribute filter's selected option ids to their product option
 * values (skipping pure content-driving attributes without a `productOption`), then:
 * - `selections`: the first selected value per option, merged with config-driven
 *   `selectedOptions` (always returned as strings).
 * - `alternateSelections`: the remaining values (joined with ", ") for multi-value
 *   options only; `selectedOptions` are excluded here as they are always single-valued.
 */
export const productOptionSelectionsWithAlternatesSelector = createSelector(
  refinementIdsByFilterSelector,
  filterOptionByIdSelector,
  selectedOptionsSelector,
  (refinements, filterOptionById, selectedOptions): { selections: StringDictionary<string>; alternateSelections: StringDictionary<string> } => {
    const { attributes } = refinements

    const { selections, alternateSelections } = Object.keys(attributes).reduce(
      (memo, key) => {
        const options = attributes[key]

        if (!options?.length) {
          return memo
        }

        let optionName: string | undefined
        const optionValues: string[] = []

        for (const optionId of options) {
          const filterOption = filterOptionById(optionId)

          if (!filterOption?.productOption) {
            continue
          }

          const { optionName: name, optionValue } = filterOption.productOption

          optionName = name
          optionValues.push(optionValue)
        }

        if (!optionName || !optionValues.length) {
          return memo
        }

        memo.selections[optionName] = optionValues[0]
        if (optionValues.length >= 2) {
          memo.alternateSelections[optionName] = optionValues.slice(1).join(', ')
        }

        return memo
      },
      { selections: {} as StringDictionary<string>, alternateSelections: {} as StringDictionary<string> },
    )

    const stringifiedSelectedOptions = Object.fromEntries(Object.entries(selectedOptions).map(([optionName, value]) => [optionName, String(value)]))

    return {
      selections: { ...selections, ...stringifiedSelectedOptions },
      alternateSelections,
    }
  },
)

export const shouldHideFbuTileSelector = createSelector(
  userOptionsSelector,
  booleanRenderPropertySelector,
  getUploadTileAvailabilitySelector,
  (userOptions, booleanRender, uploadTileAvailable) => {
    const isFoilColorSelected = !!userOptions['Foil Color'] && userOptions['Foil Color'] !== 'None'
    const isDisableFullBleedUploadForFoilColor = booleanRender(RenderProperty.DisableFullBleedUploadForFoilColor)

    return (isDisableFullBleedUploadForFoilColor && isFoilColorSelected) || !uploadTileAvailable
  },
)

export const shouldHideCareTileSelector = (state: State.GlobalState): boolean => !state.config.designServicesAvailable
